import { createContext, useReducer, useCallback } from "react";

import axios from "../../utils/axios/v1/adminAxios";

const INITIALIZE = "INITIALIZE";
const GENERATE_CERT = "GENERATE_CERT";
const VERIFY_DOMAINS = "VERIFY_DOMAINS";
const CLEAR_WILDCARD_CERTS = "CLEAR_WILDCARD_CERTS";
const SET_ERROR = "SET_ERROR";

const initialState = {
  cert_id: "",
  cname_validations: [],
  errMsg: null,
};

const GuestReducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE:
      return {
        ...state,
        cert_id: action.payload.cert_id,
        cname_validations: action.payload.cname_validations,
      };
    case GENERATE_CERT:
      return {
        ...state,
        cert_id: action.payload.cert_id,
        cname_validations: action.payload.cname_validations,
      };
    case CLEAR_WILDCARD_CERTS:
      return {
        ...state,
        cert_id: "",
        cname_validations: [],
      };
    case VERIFY_DOMAINS:
      return {};
    case SET_ERROR:
      return {
        ...state,
        errMsg: action.payload.errMsg,
      };
    default:
      return state;
  }
};

const GuestContext = createContext(null);

function GuestProvider({ children }) {
  const [state, dispatch] = useReducer(GuestReducer, initialState);

  const setErr = useCallback((msg) => {
    dispatch({
      type: SET_ERROR,
      payload: {
        errMsg: msg,
      },
    });
  }, []);

  const checkDomainName = useCallback(
    async (domain) => {
      try {
        await axios.post("/ssl_aio/check_domain", { domain });
        return { status: true };
      } catch (err) {
        setErr(err.message);
        return { status: false };
      }
    },
    [setErr]
  );

  const generateCerts = useCallback(async (domain) => {
    const res = await axios.post(`/ssl_aio/generate_certs`, {
      domain,
    });
    let cname_validations = [];
    cname_validations.push(res.data.cname_validation_p1);
    cname_validations.push(res.data.cname_validation_p2);
    let cert_id = res.data.id;
    dispatch({
      type: GENERATE_CERT,
      payload: {
        cert_id,
        cname_validations,
      },
    });
    return res.data;
  }, []);

  const verifyDomains = useCallback(async (domain, cert_id) => {
    await axios.post(`/ssl_aio/verify_domains`, {
      domain,
      cert_id,
    });
  }, []);

  const clearWildcardCerts = useCallback(() => {
    dispatch({
      type: CLEAR_WILDCARD_CERTS,
    });
  }, []);

  const uploadCerts = useCallback(
    async (value) => {
      try {
        await axios.post("/ssl_aio/upload_certs", {
          certs: value,
        });
        return true;
      } catch (err) {
        setErr(err.message);
        return false;
      }
    },
    [setErr]
  );

  const finishOnboarding = useCallback(async () => {
    try {
      axios.post("/ssl_aio/finish_onboarding");
      return true;
    } catch (err) {
      setErr(err.message);
      return false;
    }
  }, [setErr]);

  return (
    <GuestContext.Provider
      value={{
        ...state,
        clearWildcardCerts,
        checkDomainName,
        generateCerts,
        verifyDomains,
        uploadCerts,
        finishOnboarding,
        setErr,
      }}
    >
      {children}
    </GuestContext.Provider>
  );
}

export { GuestContext, GuestProvider };
